import { translate } from '@portal/services/i18n';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import QRCodeDetails from '@portal/components/QRCodeDetails/QRCodeDetails';
import { Modal, Col, Row } from 'antd';
import React, { useEffect, useState } from 'react';

import * as MessageService from '~/services/message';
import { maskCpfOrCnpj } from '@portal/services/masks';
import { TransactionType } from '@portal/models/enumerators';
import { DateTime } from 'luxon';
import { moneyFormat } from '@portal/utils/format';

type ChargeDetailsModal = {
  visible: boolean;
  setVisible: (value: boolean) => void;
  receivable: models.Receivable | null;
};

const ChargeDetailsModal = (props: ChargeDetailsModal) => {
  const initialState = {
    id: '',
    amount: 0,
    payeeName: '',
    payeeTaxIdentifier: '',
    type: 0,
    dueDate: '',
    pixKey: '',
    content: '',
    contentUrl: '',
    bankId: '',
    name: '',
    txId: '',
    transactions: [
      {
        boletoKey: '',
      },
    ],
  };
  const [receivable, setReceivable] = useState(
    props.receivable ?? initialState
  );

  const copyToClipboard = (value?: string | null) => {
    if (value) {
      navigator.clipboard.writeText(value);
      MessageService.success(
        'COMPONENTS.CHARGE_DETAILS_MODAL.MESSAGE.COPY_TO_CLIPBOARD'
      );
    }
  };

  const renderModalType = () => {
    return (
      <>
        <Row>
          <Col className="charge-details-modal__content__item">
            <b className="charge-details-modal__content__label">
              {translate('COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.AMOUNT')}
            </b>
            <p className="charge-details-modal__content__item__info">
              {moneyFormat(receivable.amount || 0)}
            </p>
          </Col>
          <Col className="charge-details-modal__content__item">
            <b className="charge-details-modal__content__label">
              {translate('COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.NAME')}
            </b>
            <p className="charge-details-modal__content__item__info">
              {receivable?.name ?? receivable.payeeName ?? ''}
            </p>
          </Col>
          <Col className="charge-details-modal__content__item">
            <b className="charge-details-modal__content__label">
              {translate(
                'COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.TAX_IDENTIFIER'
              )}
            </b>
            <p className="charge-details-modal__content__item__info">
              {maskCpfOrCnpj(
                receivable?.txId ?? receivable.payeeTaxIdentifier ?? ''
              )}
            </p>
          </Col>
          <Col className="charge-details-modal__content__item">
            <b className="charge-details-modal__content__label">
              {translate('COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.DUE_DATE')}
            </b>
            <p className="charge-details-modal__content__item__info">
              {DateTime.fromISO(receivable.dueDate ?? '').toLocaleString()}
            </p>
          </Col>
          <Col className="charge-details-modal__content__item">
            <b className="charge-details-modal__content__label">
              {translate('COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.DESCRIPTION')}
            </b>
            <p className="charge-details-modal__content__item__info">
              {receivable.transactions
                ? receivable.transactions[0].description
                : ''}
            </p>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Row>
              <Col sm={12}>
                <b className="charge-details-modal__content__label">
                  {translate(
                    'COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.CHARGE_CODE'
                  )}
                </b>
                <div className="charge-details-modal__content__item__info">
                  <AdvancedInput
                    value={receivable?.id ?? ''}
                    readOnly
                    onClick={() => copyToClipboard(receivable?.id)}
                  />
                </div>
              </Col>
            </Row>
            {receivable.pixKey ? (
              <Row>
                <Col sm={12}>
                  <b className="charge-details-modal__content__label">
                    {translate(
                      'COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.PIX_KEY'
                    )}
                  </b>
                  <div className="charge-details-modal__content__item__info">
                    <AdvancedInput
                      value={receivable.pixKey}
                      readOnly
                      onClick={() => copyToClipboard(receivable.pixKey)}
                    />
                  </div>
                </Col>
              </Row>
            ) : null}
            {receivable.type !== TransactionType.PIX && (
              <>
                <Row>
                  <Col sm={12}>
                    <b className="charge-details-modal__content__label">
                      {translate(
                        'COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.TYPEABLE_LINE'
                      )}
                    </b>
                    <div className="charge-details-modal__content__item__info">
                      <AdvancedInput
                        value={
                          receivable?.transactions
                            ? receivable?.transactions[0].boletoKey
                            : ''
                        }
                        readOnly
                        onClick={() =>
                          copyToClipboard(
                            receivable?.transactions
                              ? receivable?.transactions[0].boletoKey
                              : ''
                          )
                        }
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <div>
                      <b className="charge-details-modal__content__label">
                        {translate(
                          'COMPONENTS.CHARGE_DETAILS_MODAL.FIELDS.URL'
                        )}
                      </b>
                      <div className="charge-details-modal__content__item__info">
                        <div className="charge-details-modal__content__item__wrapper">
                          <AdvancedInput
                            fullWidth={true}
                            value={receivable.contentUrl ?? ''}
                            readOnly
                            onClick={() =>
                              copyToClipboard(receivable.contentUrl)
                            }
                          />
                          <AdvancedButton
                            text={translate(
                              'COMPONENTS.CHARGE_DETAILS_MODAL.BUTTONS.DOWNLOAD'
                            )}
                            onClick={() => {
                              window.location.assign(
                                receivable.contentUrl ?? ''
                              );
                            }}
                            disabled={!receivable.contentUrl}
                          />
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Col>
          {(receivable.type === TransactionType.PIX ||
            receivable.type === TransactionType.BILLET_PIX) && (
            <Col className="charge-details-modal__qrcode_pix mt-3" sm={5}>
              <QRCodeDetails
                content={receivable.content ?? ''}
                hideHeader
                description={false}
                buttons={true}
              />
            </Col>
          )}
        </Row>
      </>
    );
  };

  useEffect(() => {
    setReceivable(props.receivable ?? initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.receivable]);

  return (
    <Modal
      open={props.visible}
      footer={null}
      onCancel={() => props.setVisible(false)}
      width={1200}
      zIndex={90}
    >
      <div className="charge-details-modal">
        <h1 className="charge-details-modal__title">
          {translate('COMPONENTS.CHARGE_DETAILS_MODAL.TITLE')}
        </h1>
        <div className="charge-details-modal__content">{renderModalType()}</div>
      </div>
    </Modal>
  );
};

export default ChargeDetailsModal;
